import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import Tablenization from "@icoach/components/Tablenization";
import { MorePageBox, LabelHeaderCheckBox, useLabelCheckBox } from "@icoach/components/";
import { Icons, Stack, IconButton, Tooltip, Box } from "@curves/ui-base";
import { ListInfo } from "@icoach/accountBook/components";
import ScrollerArrowButton from "@icoach/inbody/components/ScrollerArrowButton";
import useMembers from "@icoach/members/components/useMembers";
import MemberDialogBox from "@icoach/members/dialog/MemberDialogBox";
import { giftDispatchByMemberList, membershipQuitByMemberList } from "@icoach/members/staticData";
import useContactGroupToggle, { contactGroupFilterTypeMap } from "@util/hook/useContactGroupToggle";
import usePrivacyToggle, { securityMaskFilterTypeMap } from "@util/hook/usePrivacyToggle";
import { getNormalLabel } from "@icoach/members/overview/NormalTemplate";
import { getFilterWorkOutTimesLabel } from "@icoach/members/overview/FilterWorkOutTimesTemplate";
import { getFilterExtensionLabel } from "@icoach/members/overview/FilterExtensionTemplate";
import { getFilterLeaveLabel } from "@icoach/members/overview/FilterLeaveTemplate";
import { getFilterInvalidLabel } from "@icoach/members/overview/FilterInvalidTemplate";
import { getFilterBirthdayLabel } from "@icoach/members/overview/FilterBirthdayTemplate";
import { getFilterNewMemberLabel } from "@icoach/members/overview/FilterNewMemberTemplate";
import { getFilterHundredTShirtLabel } from "@icoach/members/overview/FilterHundredTShirtTemplate";
import { getFilterHealthCareOrderLabel } from "@icoach/members/overview/FilterHealthCareOrderTemplate";
import { getFilterHighRiskLabel } from "@icoach/members/overview/FilterHighRiskTemplate";
import { getFilterCrossClubLabel } from "@icoach/members/overview/FilterCrossClubTemplate";
import { isEqual, isEmpty } from "@util/utils";

const quickTypeMap = {
	CrossClub: "10",
	CrossClubVisit: "16",
};

const getContactGroupType = targetType => {
	if (isEqual(String(targetType), quickTypeMap.CrossClub)) return contactGroupFilterTypeMap.CrossClub;
	if (isEqual(String(targetType), quickTypeMap.CrossClubVisit)) return null;
	return targetType;
};

// 條件篩選 or 快速名單
const OverviewTable = props => {
	/**
	 * @type {{ subTabs: "normal" | "quick" }}
	 */
	const { subTabs } = useParams();
	const { targetType } = props;
	const { refresh, sourceData, bookmark, sortOption, setBookmarkNum, setBookmarkSortBy, setContactState, reSetCheckedItemsRef, doKeepState, doCheckActiveState, setSourceDataById } = useMembers();
	const { list: rowsData = [], pageIndex: page = 1, totalPage: count, pageSize, totalCount } = sourceData;
	const { isRevealed, doTogglePrivacy, doHidePrivacy, renderPrivacyIcon } = usePrivacyToggle();
	const { isShowContactGroup, contactGroupSet, doInitContactNotify, doToggleContactGroup, doRefreshContactGroup, renderContactGroupIcon } = useContactGroupToggle();
	const memberDialogBoxRef = useRef(null);
	const memberDialogBoxRefreshRef = useRef(null);
	const scrollerArrowRef = useRef(null);
	const { sortBy } = bookmark;
	const isVisitCrossClubList = isEqual(String(targetType), quickTypeMap.CrossClubVisit);
	const contactGroupType = useMemo(() => getContactGroupType(targetType), [targetType]);
	const { checkBoxsRef, headerCheckBoxRef, onHeaderChange, onCellChange, getCheckedItems, getCheckedSourceData, doSaveCheckedItem, doClearCheckedItems } = useLabelCheckBox();

	reSetCheckedItemsRef(doClearCheckedItems);

	const rowsLabel = useMemo(
		() => {
			if (isEqual(subTabs, "normal")) {
				return getNormalLabel(isRevealed);
			} else if (isEqual(subTabs, "quick")) {
				switch (String(targetType)) {
					case "1":
						return getFilterWorkOutTimesLabel(isRevealed, isShowContactGroup);
					case "2":
						return getFilterExtensionLabel(isRevealed, isShowContactGroup);
					case "3":
						return getFilterLeaveLabel(isRevealed, isShowContactGroup);
					case "4":
						return getFilterInvalidLabel(isRevealed, isShowContactGroup);
					case "5":
						return getFilterBirthdayLabel(isRevealed, isShowContactGroup);
					case "6":
						return getFilterHundredTShirtLabel(isRevealed, isShowContactGroup);
					case "7":
						return getFilterNewMemberLabel(isRevealed, isShowContactGroup);
					case "8":
						return getFilterHealthCareOrderLabel(isRevealed, isShowContactGroup);
					case "9":
						return getFilterHighRiskLabel(isRevealed, isShowContactGroup);
					case "10":
						return getFilterCrossClubLabel(isRevealed);
					case "16":
						return getFilterCrossClubLabel(isRevealed, true);
					default:
						return getNormalLabel(isRevealed);
				}
			}
		},
		// eslint-disable-next-line
		[subTabs, targetType, isRevealed, isShowContactGroup]
	);

	const handleTogglePrivacy = useCallback(
		() => {
			const getFilterType = (subTabs, targetType) => {
				if (isEqual(subTabs, "normal")) {
					return securityMaskFilterTypeMap.ClubMemberOverview;
				} else if (isEqual(subTabs, "quick")) {
					switch (String(targetType)) {
						case "1":
							return securityMaskFilterTypeMap.ClubMemberWorkoutTimes;
						case "2":
							return securityMaskFilterTypeMap.ClubMemberRenewal;
						case "3":
							return securityMaskFilterTypeMap.ClubMemberSuspension;
						case "4":
							return securityMaskFilterTypeMap.ClubMemberInvalid;
						case "5":
							return securityMaskFilterTypeMap.ClubMemberBirthday;
						case "6":
							return securityMaskFilterTypeMap.ClubMemberHundredTShirt;
						case "7":
							return securityMaskFilterTypeMap.ClubMemberNewMember;
						case "8":
							return securityMaskFilterTypeMap.ClubMemberHealthyChoice;
						case "9":
							return securityMaskFilterTypeMap.ClubMemberHighRisk;
						case "10":
							return securityMaskFilterTypeMap.ClubMemberCrossClub;
						default:
							return securityMaskFilterTypeMap.ClubMemberWorkoutTimes;
					}
				}
			};
			const apiParams = {
				filterType: getFilterType(subTabs, targetType),
				totalCount: rowsData.length,
			};
			doTogglePrivacy(apiParams);
		},
		// eslint-disable-next-line
		[doTogglePrivacy, rowsData, subTabs, targetType]
	);

	const handleToggleContactGroup = async () => {
		await doToggleContactGroup(contactGroupType);
		if (!isRevealed) handleTogglePrivacy();
	};

	const handlePageChange = (e, num) => {
		doSaveCheckedItem();
		setBookmarkNum(num);
	};

	const handleSendSMSClick = () => {
		let params = getCheckedSourceData();
		if (params.length === 0) return;
		params = params.map(target => ({ id: target.memberID * 1, type: 2, name: target.nickName || target.name }));
		memberDialogBoxRef.current.openSmsDialog(params);
	};

	const handleGiftDispatchClick = () => {
		let params = getCheckedSourceData();
		params = params.reduce((acc, cur) => {
			return [...acc, cur.memberID];
		}, []);
		memberDialogBoxRef.current && memberDialogBoxRef.current.openGiftDispatchDialog({ memberIDs: params });
	};

	const handleBatchMembershipQuitClick = () => {
		let params = getCheckedSourceData();
		params = params.reduce((acc, cur) => {
			return [...acc, cur.memberID];
		}, []);
		memberDialogBoxRef.current.openBatchMembershipQuitDialog({ memberIDs: params }, refresh.current);
	};

	const handleKeepState = row => {
		return doKeepState && doKeepState(row.memberID);
	};

	const handleCheckActiveState = row => {
		return doCheckActiveState && doCheckActiveState(row.memberID);
	};

	const pageNumberProps = useMemo(
		() => {
			let target = {};
			target.isPagination = true;
			target.page = page;
			target.count = count;
			target.onChange = handlePageChange;
			target.Pagination = MorePageBox;
			return target;
		},
		// eslint-disable-next-line
		[subTabs, page, count]
	);

	useEffect(
		() => {
			//當聯絡標註更新時，同時更新聯絡資訊及標註
			memberDialogBoxRefreshRef.current = async () => {
				refresh && refresh.current && refresh.current();
				if (isShowContactGroup) await doRefreshContactGroup(contactGroupType);
			};
		},
		// eslint-disable-next-line
		[isShowContactGroup, contactGroupType]
	);

	useEffect(
		() => {
			doHidePrivacy();
			doInitContactNotify(contactGroupType);
		},
		// eslint-disable-next-line
		[subTabs, contactGroupType]
	);

	return (
		<Box className="customer-table">
			<Stack className={"mb-1"} alignItems={"center"} justifyContent={"space-between"}>
				{!isVisitCrossClubList && !isEmpty(rowsData) && (
					<Stack className="pl-4 pb-1">
						<LabelHeaderCheckBox key={subTabs} ref={headerCheckBoxRef} onChange={onHeaderChange} />
						<Stack>
							<Tooltip title="群發送簡訊">
								<IconButton onClick={handleSendSMSClick}>
									<Icons.PaperPlane />
								</IconButton>
							</Tooltip>
							{giftDispatchByMemberList.some(x => isEqual(x, targetType)) && (
								<Tooltip title="贈品發送">
									<IconButton onClick={handleGiftDispatchClick}>
										<Icons.GiftSolid />
									</IconButton>
								</Tooltip>
							)}
							{membershipQuitByMemberList.some(x => isEqual(x, targetType)) && (
								<Tooltip title="批次退會">
									<IconButton onClick={handleBatchMembershipQuitClick}>
										<Icons.UserMinus />
									</IconButton>
								</Tooltip>
							)}
							{renderPrivacyIcon(handleTogglePrivacy)}
							{renderContactGroupIcon(contactGroupType, handleToggleContactGroup)}
						</Stack>
					</Stack>
				)}
				<ListInfo
					className={"ml-auto"}
					pageSize={pageSize}
					pageIndex={page}
					totalCount={totalCount}
					sortDefaultValue={sortBy}
					sortOptions={sortOption}
					onSortOnChange={val => {
						doClearCheckedItems();
						setBookmarkSortBy(val);
					}}
					isSort={targetType}
					isCurrentPageLastIndex
				/>
			</Stack>
			<Box className={"scroll-arrow-container"}>
				<Tablenization {...pageNumberProps} tableContainerRef={scrollerArrowRef} cellWidth={12} isFixed>
					<Tablenization.TableRowHeader headerRow={rowsLabel} />
					<Tablenization.TableRowBody
						dialogRef={memberDialogBoxRef}
						headerRow={rowsLabel}
						rowsData={rowsData}
						targetType={targetType}
						checkBoxsRef={checkBoxsRef}
						onCheckBoxChange={onCellChange}
						getCheckedItems={getCheckedItems}
						setContactState={setContactState}
						rowOnKeepState={handleKeepState}
						rowOnCheckActiveState={handleCheckActiveState}
						setSourceDataById={setSourceDataById}
						contactData={contactGroupSet}
					/>
				</Tablenization>
				<ScrollerArrowButton parentRef={scrollerArrowRef} />
			</Box>
			<MemberDialogBox ref={memberDialogBoxRef} refresh={memberDialogBoxRefreshRef} setContactState={setContactState} />
		</Box>
	);
};

export default OverviewTable;
